import { Card, CardContent, Portal } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import type React from 'react'
import { useMemo, useState } from 'react'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import {
  aboveNotificationBar,
  type NotificationBarOffsetProps,
  useNotificationBarOffsetProps,
} from 'core/components/notification-bar/NotificationBarOffsetContext'
import SimpleLink from 'core/components/SimpleLink'
import { useIsoMountProgress } from 'core/context/isoMountProgressContext'
import BadgeWithIcon from 'core/elements/badge/BadgeWithIcon'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip'
import type Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import { IVolumeDetailsPageTabs } from 'openstack/components/storage/volumes/volume-details/model'

const ISOMountProgressPopover: React.FC = () => {
  const classes = useStyles(useNotificationBarOffsetProps())
  const [expanded, setExpanded] = useState(true)
  const { operations, clearOperations } = useIsoMountProgress()

  const operationList = useMemo(
    () => Object.values(operations).sort((a, b) => b.createdAt - a.createdAt),
    [operations],
  )

  const hasActiveOperations = useMemo(
    () => operationList.some((operation) => operation.status === 'in_progress'),
    [operationList],
  )

  if (operationList.length === 0) {
    return null
  }

  return (
    <Portal>
      <Card className={classes.card}>
        <div className={classes.header}>
          <Text variant="body1" className={classes.headerTitle}>
            Mounting ISOs ({operationList.length})
          </Text>
          <div className={classes.headerActions}>
            <FontAwesomeIcon onClick={() => setExpanded((prev) => !prev)}>
              {expanded ? 'chevron-up' : 'chevron-down'}
            </FontAwesomeIcon>
            {!hasActiveOperations && (
              <FontAwesomeIcon onClick={clearOperations}>close</FontAwesomeIcon>
            )}
          </div>
        </div>
        {expanded && (
          <CardContent className={classes.content}>
            {operationList.map((operation) => {
              const statusMeta = getStatusMeta(operation.status)
              const itemName = `${operation.vmName || operation.vmId} - ${
                operation.isoImageName || operation.isoImageId
              }`

              const failedVolumeLink =
                operation.status === 'failed' && operation.failedVolumeId
                  ? routeManager.routes.openstack.volumeDetails.path({
                      id: operation.failedVolumeId,
                      tab: IVolumeDetailsPageTabs.Overview,
                    })
                  : null

              const errorTooltip = failedVolumeLink ? (
                <span>
                  {'Error creating ISO volume. '}
                  <SimpleLink src={failedVolumeLink} className={classes.tooltipLink}>
                    More info
                  </SimpleLink>
                </span>
              ) : (
                operation.error
              )

              return (
                <div
                  key={operation.id}
                  className={clsx(classes.row, operationList.length > 1 && classes.rowBorder)}
                >
                  <Tooltip message={itemName}>
                    <Text variant="body2" className={classes.rowName}>
                      {itemName}
                    </Text>
                  </Tooltip>
                  <div className={classes.rowActions}>
                    {operation.error ? (
                      <Tooltip message={errorTooltip}>
                        <div>
                          <BadgeWithIcon
                            text={statusMeta.text}
                            variant={statusMeta.variant}
                            icon={statusMeta.icon}
                            spin={statusMeta.spin}
                          />
                        </div>
                      </Tooltip>
                    ) : (
                      <BadgeWithIcon
                        text={statusMeta.text}
                        variant={statusMeta.variant}
                        icon={statusMeta.icon}
                        spin={statusMeta.spin}
                      />
                    )}
                  </div>
                </div>
              )
            })}
          </CardContent>
        )}
      </Card>
    </Portal>
  )
}

const getStatusMeta = (status: 'in_progress' | 'completed' | 'failed') => {
  switch (status) {
    case 'completed':
      return { text: 'Completed', variant: 'success' as const, icon: 'check', spin: false }
    case 'failed':
      return { text: 'Failed', variant: 'error' as const, icon: 'xmark', spin: false }
    case 'in_progress':
    default:
      return { text: 'Ongoing', variant: 'primary' as const, icon: 'arrows-rotate', spin: true }
  }
}

const useStyles = makeStyles<Theme, NotificationBarOffsetProps>((theme) => ({
  card: {
    position: 'fixed',
    // NB-FR-001 (NotificationBar.spec.md): above the admin notification bar and its drawer, so
    // it no longer covers the bar's controls
    bottom: aboveNotificationBar(theme.spacing(3)),
    right: theme.spacing(3),
    width: 460,
    maxWidth: 'calc(100vw - 32px)',
    zIndex: 1200,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.grey[100],
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerTitle: {
    fontWeight: 600,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  content: {
    padding: theme.spacing(0),
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1.5),
  },
  rowBorder: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  rowName: {
    maxWidth: 260,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'help',
  },
  rowActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  tooltipLink: {
    '& .simple-link-text': {
      color: theme.palette.common.white,
      textDecoration: 'underline',
    },
    '&:hover .simple-link-text': {
      color: theme.palette.common.white,
    },
  },
}))

export default ISOMountProgressPopover
